/** Модалка «Сформировать рейс» — оператор обрабатывает заявку о прибытии
 *  (DRIVER_WALKUP_REQUEST): формирует новый запланированный рейс либо
 *  привязывает заявку к существующему запланированному рейсу, дозаполняя
 *  недостающие поля. При успехе заявка помечается обработанной, а водителю (по
 *  телефону из заявки) уходит уведомление о назначении рейса.
 */
import { type Component, For, Show, createMemo, createSignal } from 'solid-js'
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query'
import toast from '~/lib/toast'
import { ModalShell } from '~/components/ui/ModalShell'
import { api, type ApiError } from '~/lib/api'

interface Props {
  open: boolean
  onClose: () => void
  /** ID исходного сообщения-заявки (DRIVER_WALKUP_REQUEST). */
  messageId: number
  /** Payload заявки (поля формы + коды справочников). */
  payload: Record<string, unknown> | null | undefined
  locationId: number
  /** Вызывается при успешном формировании/привязке рейса (до onClose). */
  onSuccess?: () => void
}

interface RefItem {
  id: number
  code: string
  name: string
  direction?: 'in' | 'out' | null
}
interface UnitItem {
  id: number
  shortname: string
  fullname: string
}
interface CounterpartyItem {
  id: number
  display_name: string
}
interface PlannedCruise {
  id: number
  name: string
  points_cnt: number
  plan_begin_service: string | null
  vehicle_number: string
  operation_type_name: string
  cargo_mode_name: string
}

const str = (v: unknown): string => (v == null ? '' : String(v))

export const FormCruiseModal: Component<Props> = (props) => {
  const qc = useQueryClient()
  const payload = () => props.payload || {}

  const [mode, setMode] = createSignal<'new' | 'link'>('new')
  const [operationCode, setOperationCode] = createSignal(str(payload().operation_type_code))
  const [cargoModeCode, setCargoModeCode] = createSignal(str(payload().cargo_mode_code))
  const [unitId, setUnitId] = createSignal(str(payload().unit_id))
  const [amount, setAmount] = createSignal(str(payload().amount))
  const [vehicle, setVehicle] = createSignal(str(payload().vehicle_number))
  const [counterpartyName, setCounterpartyName] = createSignal(str(payload().counterparty_name))
  const [cruiseId, setCruiseId] = createSignal<number | null>(null)

  const opTypesQuery = createQuery(() => ({
    queryKey: ['operation-types'],
    queryFn: () => api.get<RefItem[]>('/api/cargo/operation-types/'),
  }))
  const cargoModesQuery = createQuery(() => ({
    queryKey: ['cargo-modes'],
    queryFn: () => api.get<RefItem[]>('/api/cargo/cargo-modes/'),
  }))
  const unitsQuery = createQuery(() => ({
    queryKey: ['cargo-units'],
    queryFn: () => api.get<UnitItem[]>('/api/cargo/units/'),
  }))
  const counterpartiesQuery = createQuery(() => ({
    queryKey: ['walkup-counterparties', props.locationId],
    queryFn: () =>
      api.get<{ results: CounterpartyItem[] }>(
        `/api/identity/account/counterparties/?location_id=${props.locationId}`,
      ),
  }))
  const plannedQuery = createQuery(() => ({
    queryKey: ['walkup-planned-cruises', props.locationId],
    queryFn: () =>
      api.get<{ items: PlannedCruise[] }>(
        `/api/cargo/walkup/planned-cruises/?location_id=${props.locationId}`,
      ),
    enabled: mode() === 'link',
  }))

  const opTypes = createMemo(() => opTypesQuery.data ?? [])
  const cargoModes = createMemo(() => cargoModesQuery.data ?? [])
  const units = createMemo(() => unitsQuery.data ?? [])
  const counterparties = createMemo(() => counterpartiesQuery.data?.results ?? [])
  const planned = createMemo(() => plannedQuery.data?.items ?? [])

  // Направление выбранной цели прибытия → роль контрагента.
  const counterpartyLabel = createMemo(() => {
    const dir = opTypes().find((o) => o.code === operationCode())?.direction
    if (dir === 'out') return 'Получатель'
    if (dir === 'in') return 'Отправитель'
    return 'Отправитель / Получатель'
  })

  const submitMut = createMutation(() => ({
    mutationFn: () =>
      api.post<{ cruise_id: number; cruise_name: string; driver_notified: string }>(
        `/api/cargo/walkup/${props.messageId}/form-cruise/`,
        {
          mode: mode(),
          ...(mode() === 'link' ? { cruise_id: cruiseId() } : {}),
          overrides: {
            operation_type_code: operationCode(),
            cargo_mode_code: cargoModeCode(),
            unit_id: unitId(),
            amount: amount(),
            vehicle_number: vehicle(),
            counterparty_name: counterpartyName().trim(),
            counterparty_id: (() => {
              const n = counterpartyName().trim().toLowerCase()
              const m = counterparties().find((c) => c.display_name.toLowerCase() === n)
              return m ? String(m.id) : ''
            })(),
          },
        },
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['device-messages-queue'] })
      const notified = res.driver_notified === 'sent'
      toast.success(
        notified
          ? 'Рейс сформирован, водитель уведомлён'
          : 'Рейс сформирован',
      )
      props.onSuccess?.()
      props.onClose()
    },
    onError: (e: ApiError) => toast.error(e.detail || 'Ошибка'),
  }))

  const canSave = createMemo(() => {
    if (submitMut.isPending) return false
    if (mode() === 'new') return !!vehicle().trim() && !!operationCode()
    return cruiseId() !== null
  })

  const handleSave = () => {
    if (mode() === 'new' && !operationCode()) {
      toast.error('Выберите цель прибытия')
      return
    }
    if (mode() === 'link' && cruiseId() === null) {
      toast.error('Выберите рейс для привязки')
      return
    }
    submitMut.mutate()
  }

  return (
    <ModalShell
      open={props.open}
      onClose={props.onClose}
      title="Сформировать рейс из заявки"
      size="lg"
      onSave={handleSave}
      saveDisabled={!canSave()}
      savePending={submitMut.isPending}
      saveLabel={mode() === 'link' ? 'Привязать' : 'Сформировать'}
    >
      <div class="space-y-4">
        {/* Сводка заявки */}
        <div class="rounded-md border border-border-faint bg-surface-2 p-3 text-sm">
          <div class="grid grid-cols-2 gap-x-4 gap-y-1">
            <Summary label="Номер ТС" value={str(payload().vehicle_number)} />
            <Summary label="Телефон" value={str(payload().phone)} />
            <Summary label="Цель прибытия" value={str(payload().operation_type_name)} />
            <Summary
              label={str(payload().counterparty_role) || 'Контрагент'}
              value={str(payload().counterparty_name)}
            />
            <Summary label="Тип груза" value={str(payload().cargo_mode_name)} />
            <Summary
              label="Количество"
              value={[str(payload().amount), str(payload().unit_name)].filter(Boolean).join(' ')}
            />
          </div>
        </div>

        {/* Режим */}
        <div class="flex gap-2">
          <ModeButton active={mode() === 'new'} onClick={() => setMode('new')}>
            Новый рейс
          </ModeButton>
          <ModeButton active={mode() === 'link'} onClick={() => setMode('link')}>
            Привязать к запланированному
          </ModeButton>
        </div>

        <Show when={mode() === 'new'}>
          <div class="space-y-3">
            <div class="grid grid-cols-2 gap-3">
              <TextField label="Номер ТС *" value={vehicle()} onInput={setVehicle} />
              <SelectField
                label="Цель прибытия *"
                value={operationCode()}
                onChange={setOperationCode}
                options={opTypes().map((o) => ({ value: o.code, label: o.name || o.code }))}
              />
            </div>
            <SelectField
              label="Тип груза"
              value={cargoModeCode()}
              onChange={setCargoModeCode}
              options={cargoModes().map((c) => ({ value: c.code, label: c.name || c.code }))}
            />
            <SuggestField
              label={counterpartyLabel()}
              value={counterpartyName()}
              onInput={setCounterpartyName}
              listId="form-cruise-counterparties"
              options={counterparties().map((c) => c.display_name)}
            />
            <div class="grid grid-cols-2 gap-3">
              <TextField label="Количество" value={amount()} onInput={setAmount} inputMode="numeric" />
              <SelectField
                label="Единица измерения"
                value={unitId()}
                onChange={setUnitId}
                options={units().map((u) => ({
                  value: String(u.id),
                  label: u.fullname ? `${u.shortname} · ${u.fullname}` : u.shortname,
                }))}
              />
            </div>
          </div>
        </Show>

        <Show when={mode() === 'link'}>
          <div class="space-y-2">
            <div class="text-xs uppercase tracking-wide text-ink-faint">
              Запланированные рейсы локации
            </div>
            <Show
              when={planned().length > 0}
              fallback={
                <div class="rounded-md border border-border-faint bg-surface-2 p-4 text-center text-sm text-ink-muted">
                  Нет запланированных рейсов.
                </div>
              }
            >
              <div class="max-h-64 space-y-2 overflow-y-auto">
                <For each={planned()}>
                  {(c) => (
                    <button
                      type="button"
                      class="w-full rounded-md border px-3 py-2 text-left text-sm transition-colors"
                      classList={{
                        'border-info bg-info-soft': cruiseId() === c.id,
                        'border-border bg-surface hover:bg-surface-hover': cruiseId() !== c.id,
                      }}
                      onClick={() => setCruiseId(c.id)}
                    >
                      <div class="font-medium text-ink">{c.name}</div>
                      <div class="text-xs text-ink-muted">
                        {[
                          c.vehicle_number,
                          c.operation_type_name,
                          c.cargo_mode_name,
                          `точек: ${c.points_cnt}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </button>
                  )}
                </For>
              </div>
            </Show>
            <p class="text-xs text-ink-muted">
              Недостающие поля рейса (номер ТС, тип груза, время) будут заполнены из
              заявки; груз заявки добавится к рейсу.
            </p>
          </div>
        </Show>
      </div>
    </ModalShell>
  )
}

const Summary: Component<{ label: string; value: string }> = (props) => (
  <div>
    <div class="text-2xs uppercase tracking-wide text-ink-faint">{props.label}</div>
    <div class="text-ink truncate">{props.value || '—'}</div>
  </div>
)

const ModeButton: Component<{ active: boolean; onClick: () => void; children: any }> = (props) => (
  <button
    type="button"
    class="flex-1 rounded-md border px-3 py-2 text-sm transition-colors"
    classList={{
      'border-info bg-info text-white': props.active,
      'border-border bg-surface text-ink hover:bg-surface-hover': !props.active,
    }}
    onClick={props.onClick}
  >
    {props.children}
  </button>
)

const TextField: Component<{
  label: string
  value: string
  onInput: (v: string) => void
  inputMode?: 'numeric' | 'text' | 'tel'
}> = (props) => (
  <div>
    <label class="mb-1.5 block text-xs uppercase tracking-wide text-ink-faint">{props.label}</label>
    <input
      type="text"
      inputMode={props.inputMode}
      class="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-ink focus:border-info focus:outline-none"
      value={props.value}
      onInput={(e) => props.onInput(e.currentTarget.value)}
    />
  </div>
)

const SuggestField: Component<{
  label: string
  value: string
  onInput: (v: string) => void
  listId: string
  options: string[]
}> = (props) => (
  <div>
    <label class="mb-1.5 block text-xs uppercase tracking-wide text-ink-faint">{props.label}</label>
    <input
      type="text"
      list={props.listId}
      class="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-ink focus:border-info focus:outline-none"
      value={props.value}
      onInput={(e) => props.onInput(e.currentTarget.value)}
      placeholder="Начните вводить название…"
      autocomplete="off"
    />
    <datalist id={props.listId}>
      <For each={props.options}>{(o) => <option value={o} />}</For>
    </datalist>
  </div>
)

const SelectField: Component<{
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}> = (props) => (
  <div>
    <label class="mb-1.5 block text-xs uppercase tracking-wide text-ink-faint">{props.label}</label>
    <select
      class="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-ink focus:border-info focus:outline-none disabled:opacity-50"
      value={props.value}
      onChange={(e) => props.onChange(e.currentTarget.value)}
      disabled={props.options.length === 0}
    >
      <option value="">— выберите —</option>
      <For each={props.options}>{(opt) => <option value={opt.value}>{opt.label}</option>}</For>
    </select>
  </div>
)
